import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface PageHeader {
  title: string | null;
  description: string | null;
  /** Optional controls rendered centered in the top bar (desktop), e.g. the
   *  report's День/Неделя + date picker. */
  center: React.ReactNode | null;
  /** Компактная версия center для мобильной шапки (иконка-кнопка периода):
   *  встаёт рядом с выбором филиала, а не падает в тело страницы (Алексей 17.09). */
  centerMobile: React.ReactNode | null;
  /** Optional right-aligned actions in the top bar (e.g. an (i) button), shown
   *  instead of the default title block when present. */
  actions: React.ReactNode | null;
  /** Optional extra content shown inside the mobile header info (i) popover,
   *  under the title/description (e.g. the dashboard's "что ещё" hint). */
  extra: React.ReactNode | null;
  /** Ссылка на настройки раздела: на телефоне у заголовка вместо (i) встаёт
   *  маленькая шестерёнка, в её подсказке — кнопка «Настройки» (AI12.6). */
  settingsTo: string | null;
}

interface PageHeaderContextValue extends PageHeader {
  setHeader: (next: Partial<PageHeader>) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: React.ReactNode }) {
  const [header, setHeaderState] = useState<PageHeader>({
    title: null,
    description: null,
    center: null,
    centerMobile: null,
    actions: null,
    extra: null,
    settingsTo: null,
  });
  const setHeader = useCallback(
    (next: Partial<PageHeader>) => setHeaderState((prev) => ({ ...prev, ...next })),
    [],
  );
  return (
    <PageHeaderContext.Provider value={{ ...header, setHeader }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeader() {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) throw new Error('usePageHeader must be used inside <PageHeaderProvider>');
  return ctx;
}

// Convenience hook for pages: declare title/description once, clear on unmount.
export function usePageTitle(title: string, description?: string | null) {
  const { setHeader } = usePageHeader();
  useEffect(() => {
    setHeader({ title, description: description ?? null });
    return () => setHeader({ title: null, description: null });
  }, [setHeader, title, description]);
}

// Inject centered controls + right-aligned actions into the top bar. `deps` are
// the primitive values the slots depend on (the slots are fresh ReactNodes each
// render, so they can't be effect deps directly).
export function usePageHeaderSlots(
  slots: {
    center?: React.ReactNode; centerMobile?: React.ReactNode; actions?: React.ReactNode; extra?: React.ReactNode;
    settingsTo?: string | null;
  },
  deps: React.DependencyList,
) {
  const { setHeader } = usePageHeader();
  useEffect(() => {
    setHeader({
      center: slots.center ?? null, centerMobile: slots.centerMobile ?? null,
      actions: slots.actions ?? null, extra: slots.extra ?? null, settingsTo: slots.settingsTo ?? null,
    });
    return () => setHeader({ center: null, centerMobile: null, actions: null, extra: null, settingsTo: null });
  }, [setHeader, ...deps]);
}
